import { useEffect, useState } from "react";
import { AlertTriangle, BookOpen, Plus, X as XIcon } from "lucide-react";
import { platformApi } from "../../lib/api";

const CATEGORIES = [
  "property_identity_location","arrival_guidance","checkout_guidance","wifi",
  "house_rules","heating_essential_appliances","waste_disposal",
  "emergency_contacts_guidance","local_essentials","operational_escalation_route",
  "general_faq","local_recommendation",
];
const SCOPES = ["brand", "property", "neighbourhood"];
const CRITICALITIES = ["standard", "operational", "safety_critical"];
const NOTICE = "Text-only content. Published versions are immutable; edits create a new Draft.";

export default function KnowledgeBasePanel() {
  const [items, setItems] = useState([]);
  const [brand, setBrand] = useState(null);
  const [editing, setEditing] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [b, kb] = await Promise.all([
        platformApi.get("/brand").then(r => r.data),
        platformApi.get("/knowledge-items?limit=100").then(r => r.data),
      ]);
      setBrand(b); setItems(kb.items || []);
    } catch (e) { setError(e?.response?.data?.message || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-knowledge-base">
      <header className="mb-7 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="ed-overline mb-2 flex items-center gap-2">
            <BookOpen size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" /> System
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Knowledge Base</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">{NOTICE}</p>
        </div>
        <button data-testid="kb-new" onClick={() => setEditing({})} disabled={!brand}
                className="text-[0.7rem] uppercase tracking-[0.22em] py-2.5 px-4 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] rounded-sm flex items-center gap-2 disabled:opacity-40">
          <Plus size={14} /> New Content
        </button>
      </header>

      {error && <div role="alert" className="mb-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm">{error}</div>}
      {loading && <p className="text-sm text-[var(--ed-ink-muted)]">Loading…</p>}

      <div className="border border-[var(--ed-rule)] rounded-sm bg-white">
        {!loading && items.length === 0 && (
          <p data-testid="kb-empty" className="p-8 text-center text-sm text-[var(--ed-ink-muted)]">No knowledge items yet.</p>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kb-table">
              <thead className="bg-[var(--ed-paper)]">
                <tr className="text-left text-[var(--ed-ink-muted)]">
                  {["Title","Scope","Category","State","Updated","Actions"].map(h =>
                    <th key={h} className="px-4 py-2.5 ed-overline text-[0.55rem]">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-[var(--ed-rule)] hover:bg-[var(--ed-paper)]/50">
                    <td className="px-4 py-2.5 text-[var(--ed-ink)]">
                      <div>{it.title}</div>
                      <div className="text-[0.7rem] font-mono text-[var(--ed-ink-muted)]">{it.id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[var(--ed-ink-muted)]">{it.scopeType}</td>
                    <td className="px-4 py-2.5 text-[var(--ed-ink-muted)]">{it.category}</td>
                    <td className="px-4 py-2.5"><StatePill state={it.state} /></td>
                    <td className="px-4 py-2.5 font-mono text-[0.75rem] text-[var(--ed-ink-muted)]">{(it.updatedAt||"").replace("T"," ").slice(0,19)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-2 flex-wrap">
                        <button data-testid={`kb-edit-${it.id}`} onClick={() => setEditing({ item: it })} className={btn}>Edit</button>
                        <button data-testid={`kb-history-${it.id}`} onClick={() => setHistoryOf(it.id)} className={btn}>History</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && brand && (
        <Editor brand={brand} initial={editing.item} onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }} />
      )}
      {historyOf && (
        <History itemId={historyOf} onClose={() => setHistoryOf(null)} onRestored={load} />
      )}
    </div>
  );
}

function Editor({ brand, initial, onClose, onSaved }) {
  const [full, setFull] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (!editing) { setForm({ scopeType:"property", category:"wifi", safetyCriticality:"standard", contentText:"", title:"" }); return; }
    (async () => {
      try {
        const r = await platformApi.get(`/knowledge-items/${initial.id}`).then(r => r.data);
        setFull(r);
        const v = r.draft || r.current || {};
        setForm({
          title: r.item.title || "",
          scopeType: r.item.scopeType,
          scopeId: r.item.scopeId || "",
          category: r.item.category,
          contentText: v.contentText || "",
          sourceReference: v.sourceReference || "",
          ownerId: v.ownerId || "",
          reviewDueAt: (v.reviewDueAt || "").slice(0,10),
          safetyCriticality: v.safetyCriticality || "standard",
        });
      } catch (e) { setError(e?.response?.data?.message || "Load failed"); }
    })();
  }, [initial, editing]);

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      if (!editing) {
        await platformApi.post("/knowledge-items", {
          brandId: brand.id, scopeType: form.scopeType,
          scopeId: form.scopeType === "brand" ? null : form.scopeId,
          category: form.category, title: form.title, locale: "en-GB",
          contentText: form.contentText, sourceReference: form.sourceReference || null,
          ownerId: form.ownerId || null,
          reviewDueAt: form.reviewDueAt ? new Date(form.reviewDueAt).toISOString() : null,
          safetyCriticality: form.safetyCriticality,
        });
      } else {
        await platformApi.patch(`/knowledge-items/${initial.id}/draft`, {
          title: form.title, contentText: form.contentText,
          sourceReference: form.sourceReference || null,
          ownerId: form.ownerId || null,
          reviewDueAt: form.reviewDueAt ? new Date(form.reviewDueAt).toISOString() : null,
          safetyCriticality: form.safetyCriticality,
        });
      }
      onSaved();
    } catch (e) { setError(e?.response?.data?.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true); setError(null);
    try {
      await platformApi.post(`/knowledge-items/${initial.id}/publish`);
      onSaved();
    } catch (e) { setError(e?.response?.data?.message || "Publish failed"); }
    finally { setBusy(false); }
  };
  const archive = async () => {
    setBusy(true); setError(null);
    try {
      await platformApi.post(`/knowledge-items/${initial.id}/archive`);
      onSaved();
    } catch (e) { setError(e?.response?.data?.message || "Archive failed"); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="true" data-testid="kb-editor"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={save} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--ed-rule)] rounded-sm">
        <div className="px-5 py-4 border-b border-[var(--ed-rule)] flex justify-between items-center">
          <h2 className="font-serif text-xl">{editing ? "Edit Content" : "New Content"}</h2>
          <button type="button" onClick={onClose} aria-label="Close"><XIcon size={16} /></button>
        </div>
        {error && <div role="alert" className="m-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm">{error}</div>}
        <div className="p-5 space-y-3">
          <Field label="Title" required>
            <input required maxLength={200} className={fieldCls}
                   value={form.title||""} onChange={e=>setForm(f=>({...f,title:e.target.value}))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Scope">
              <select className={fieldCls} value={form.scopeType||"property"} onChange={e=>setForm(f=>({...f,scopeType:e.target.value}))}>
                {SCOPES.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select className={fieldCls} value={form.category||"wifi"} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          {form.scopeType !== "brand" && (
            <Field label={`${form.scopeType} ID`}>
              <input className={fieldCls} value={form.scopeId||""} onChange={e=>setForm(f=>({...f,scopeId:e.target.value}))}
                     placeholder={form.scopeType==="property" ? "prp_..." : "neighbourhood id"} />
            </Field>
          )}
          <Field label="Content (text only)" required>
            <textarea data-testid="kb-content" required rows={8} maxLength={16000} className={fieldCls}
                      value={form.contentText||""} onChange={e=>setForm(f=>({...f,contentText:e.target.value}))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source reference">
              <input className={fieldCls} value={form.sourceReference||""} onChange={e=>setForm(f=>({...f,sourceReference:e.target.value}))} />
            </Field>
            <Field label="Owner ID">
              <input className={fieldCls} value={form.ownerId||""} onChange={e=>setForm(f=>({...f,ownerId:e.target.value}))} />
            </Field>
            <Field label="Review due">
              <input type="date" className={fieldCls} value={form.reviewDueAt||""} onChange={e=>setForm(f=>({...f,reviewDueAt:e.target.value}))} />
            </Field>
            <Field label="Safety criticality">
              <select className={fieldCls} value={form.safetyCriticality||"standard"} onChange={e=>setForm(f=>({...f,safetyCriticality:e.target.value}))}>
                {CRITICALITIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <p className="text-[0.7rem] text-[var(--ed-ink-muted)]">
            Safety-critical items require source, owner and review-due date before publish.
          </p>
        </div>
        <div className="px-5 py-4 border-t border-[var(--ed-rule)] flex justify-between gap-3 flex-wrap">
          <div className="flex gap-2">
            {editing && <button type="button" disabled={busy} onClick={publish} data-testid="kb-publish" className={btnEmerald}>Publish</button>}
            {editing && <button type="button" disabled={busy} onClick={archive} data-testid="kb-archive" className={btn}>Archive</button>}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={btn}>Cancel</button>
            <button type="submit" disabled={busy} data-testid="kb-save" className={btnInk}>{busy ? "Saving…" : editing ? "Save Draft" : "Create"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

function History({ itemId, onClose, onRestored }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => { (async () => {
    try { setRows((await platformApi.get(`/knowledge-items/${itemId}/versions`)).data.items); }
    catch (e) { setError(e?.response?.data?.message || "Failed"); }
  })(); }, [itemId]);

  const restore = async (vid) => {
    try {
      await platformApi.post(`/knowledge-items/${itemId}/restore/${vid}`);
      onRestored(); onClose();
    } catch (e) { setError(e?.response?.data?.message || "Restore failed"); }
  };

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-start justify-end bg-black/40">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto border-l border-[var(--ed-rule)]">
        <div className="px-5 py-4 border-b border-[var(--ed-rule)] flex justify-between items-center">
          <h2 className="font-serif text-xl">Version history</h2>
          <button onClick={onClose} aria-label="Close"><XIcon size={16} /></button>
        </div>
        {error && <div role="alert" className="m-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm">{error}</div>}
        <ul className="divide-y divide-[var(--ed-rule)]">
          {rows.map(v => (
            <li key={v.id} className="p-4">
              <p className="text-sm">v{v.versionNumber} · <StatePill state={v.publicationState} /> · <span className="text-[0.65rem] uppercase tracking-[0.2em]">{v.safetyCriticality}</span></p>
              <p className="text-[0.7rem] font-mono text-[var(--ed-ink-muted)] mt-1">{(v.createdAt||"").replace("T"," ").slice(0,19)} · by {v.createdBy||"—"}</p>
              <button onClick={() => restore(v.id)} className={btn + " mt-2"}>Restore as new Draft</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const fieldCls = "w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm";
const btn = "text-[0.7rem] uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm";
const btnInk = "text-[0.7rem] uppercase tracking-[0.22em] px-5 py-2 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] rounded-sm disabled:opacity-40";
const btnEmerald = "text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 bg-[var(--ed-emerald)] text-white hover:opacity-90 rounded-sm disabled:opacity-40";
function Field({ label, required, children }) {
  return <div><label className="ed-overline text-[0.55rem] mb-1.5 block">{label}{required && <span className="text-red-600 ml-0.5">*</span>}</label>{children}</div>;
}
function StatePill({ state }) {
  const map = { draft:"bg-neutral-100 text-neutral-800 border-neutral-300",
                published:"bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)]",
                archived:"bg-amber-50 text-amber-800 border-amber-300" };
  return <span className={`inline-block text-[0.6rem] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${map[state] || ""}`}>{state}</span>;
}
